import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  ListItemButton,
  Checkbox,
  ListSubheader,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
  Paper,
  styled,
} from "@mui/material";
import { groupBy } from "lodash";
import { ReactElement, useState } from "react";
import {
  FieldPathByValue,
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { isUUID } from "@stw/uuid";

import {
  AutocompleteCampaignSearchQuery,
  AutocompleteCampaignSearchQueryVariables,
  AutocompleteSubaccountUserSearchQuery,
  AutocompleteSubaccountUserSearchQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import FormTextField from "~frontend/components/forms/FormTextField";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";
import { AUTOCOMPLETE_CAMPAIGN_SEARCH } from "~frontend/graphql/campaigns";
import { AUTOCOMPLETE_SUBACCOUNT_USER_SEARCH } from "~frontend/graphql/users";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useDebounce from "~frontend/util/useDebounce";

import { ThreadFilterAndSortValues } from "./schema";

type CampaignAndTexterSearchOption =
  ThreadFilterAndSortValues["campaignsAndTexters"][number];

const AutocompletePaper = styled(Paper)({
  "& .MuiAutocomplete-option[aria-selected='true']": {
    backgroundColor: palette.lightGray,
    "&.Mui-focused": {
      backgroundColor: palette.lightGray,
    },
  },
});

export function RhfCampaignsAndTextersField<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, CampaignAndTexterSearchOption[]>,
>(props: UseControllerProps<TFieldValues, TName>): ReactElement {
  const intl = useIntl();
  const { subaccount } = useSubaccount()!;

  const { field } = useController(props);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const { loading: campaignsLoading, data: campaignsData } = useQuery<
    AutocompleteCampaignSearchQuery,
    AutocompleteCampaignSearchQueryVariables
  >(AUTOCOMPLETE_CAMPAIGN_SEARCH, {
    variables: {
      subaccountId: subaccount.id,
      search: {
        excludeUnstarted: true,
        ...(isUUID(debouncedSearch)
          ? { idIn: [debouncedSearch.trim().toLowerCase()] }
          : { nameContains: debouncedSearch }),
      },
    },
    skip: debouncedSearch.length < 3,
  });

  const { loading: textersLoading, data: textersData } = useQuery<
    AutocompleteSubaccountUserSearchQuery,
    AutocompleteSubaccountUserSearchQueryVariables
  >(AUTOCOMPLETE_SUBACCOUNT_USER_SEARCH, {
    variables: {
      subaccountId: subaccount.id,
      search: { nameOrEmailContains: debouncedSearch },
    },
    skip: debouncedSearch.length < 3,
  });

  function getOptionLabel(option: string | CampaignAndTexterSearchOption) {
    if (typeof option === "string") {
      return option;
    }
    return option.name;
  }

  function getOptionKey(option: string | CampaignAndTexterSearchOption) {
    if (typeof option === "string") {
      return option;
    }
    return option.id;
  }

  const selected = field.value as CampaignAndTexterSearchOption[] | null;

  const selectedCampaignOptions =
    selected?.filter(option => option.type === "campaign") ?? [];
  const selectedTexterOptions =
    selected?.filter(option => option.type === "texter") ?? [];

  const autocompleteOptions: CampaignAndTexterSearchOption[] = [];

  if (
    selectedCampaignOptions.length > 0 ||
    (campaignsData && campaignsData.listCampaigns.campaigns.length > 0)
  ) {
    const selectedCampaignIds = new Set(
      selectedCampaignOptions.map(campaign => campaign.id)
    );
    autocompleteOptions.push(
      ...selectedCampaignOptions,
      ...(campaignsData?.listCampaigns.campaigns
        .filter(campaign => !selectedCampaignIds.has(campaign.id))
        .map(
          campaign =>
            ({
              type: "campaign",
              id: campaign.id,
              name: campaign.name,
            }) as CampaignAndTexterSearchOption
        ) ?? [])
    );
  }

  if (
    selectedTexterOptions.length > 0 ||
    (textersData && textersData.listSubaccountUsers.users.length > 0)
  ) {
    const selectedTexterIds = new Set(
      selectedTexterOptions.map(texter => texter.id)
    );
    autocompleteOptions.push(
      ...selectedTexterOptions,
      ...(textersData?.listSubaccountUsers.users
        .filter(user => !selectedTexterIds.has(user.id))
        .map(
          user =>
            ({
              type: "texter",
              id: user.id,
              name: user.fullName,
            }) as CampaignAndTexterSearchOption
        ) ?? [])
    );
  }

  function campaignOrTexterFilterLabel(
    options: CampaignAndTexterSearchOption[],
    type: string
  ) {
    if (!options) {
      return null;
    }

    if (options.length > 2) {
      switch (type) {
        case "campaign":
          return (
            <FormattedMessage
              defaultMessage={"({count}) Campaigns"}
              description="Label for campaign-based search box, when more than 2 are selected."
              values={{
                count: options.length,
              }}
            />
          );
        case "texter":
          return (
            <FormattedMessage
              defaultMessage={"({count}) Texters"}
              description="Label for texter-based search box, when more than 2 are selected."
              values={{
                count: options.length,
              }}
            />
          );
      }
    }

    return options.map(opt => getOptionLabel(opt)).join(", ");
  }

  return (
    <Autocomplete
      sx={{ width: "50%" }}
      multiple
      value={field.value}
      onChange={(_event, value) => field.onChange(value)}
      disableCloseOnSelect
      onBlur={() => {
        setSearch("");
        field.onBlur();
      }}
      noOptionsText={
        search.length < 3
          ? intl.formatMessage({
              defaultMessage: "Enter at least 3 characters to search...",
              description: "Prompt to enter a search string",
            })
          : intl.formatMessage({
              defaultMessage: "No matching campaigns or texters found",
              description: "Message when no campaigns or texters were found",
            })
      }
      isOptionEqualToValue={(option, value) => {
        switch (option.type) {
          case "campaign":
            return value.type === "campaign" && value.id === option.id;
          case "texter":
            return value.type === "texter" && value.id === option.id;
          default:
            return false;
        }
      }}
      renderOption={(props, option, { selected }) => (
        <ListItemButton
          key={getOptionKey(option)}
          {...(props as unknown as React.HTMLAttributes<HTMLDivElement>)}
        >
          <Checkbox checked={selected} icon={<CheckBoxUnchecked />} />
          {getOptionLabel(option)}
        </ListItemButton>
      )}
      options={autocompleteOptions}
      filterOptions={x => x}
      groupBy={option => option.type}
      renderGroup={params => {
        let groupName: string;
        switch (params.group as CampaignAndTexterSearchOption["type"]) {
          case "campaign":
            groupName = intl.formatMessage({
              defaultMessage: "Campaigns",
              description: "Label for campaign options",
            });
            break;
          case "texter":
            groupName = intl.formatMessage({
              defaultMessage: "Texters",
              description: "Label for texter options",
            });
        }
        return (
          <>
            <ListSubheader>
              <Typography variant="h4Caps" sx={{ color: palette.darkGray }}>
                {groupName}
              </Typography>
            </ListSubheader>
            {params.children}
          </>
        );
      }}
      loading={
        search.length >= 3 &&
        (campaignsLoading ||
          textersLoading ||
          debouncedSearch !== search ||
          selectedCampaignOptions.some(campaign => !campaign.name) ||
          selectedTexterOptions.some(texter => !texter.name))
      }
      loadingText={
        <FormattedMessage
          defaultMessage="Loading..."
          description="Loading text"
        />
      }
      renderTags={value => {
        if (value.some(option => !getOptionLabel(option))) {
          return (
            <Loading
              size={20}
              style={{ marginTop: 0, marginLeft: theme.spacing(1) }}
            />
          );
        }
        const optionsByType = groupBy(value, "type");
        const campaignFilterLabel = campaignOrTexterFilterLabel(
          optionsByType["campaign"],
          "campaign"
        );
        const texterFilterLabel = campaignOrTexterFilterLabel(
          optionsByType["texter"],
          "texter"
        );

        return (
          <Typography
            variant="body2"
            component={"div"}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "75%",
            }}
          >
            <FlexBox gap={1}>
              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {campaignFilterLabel}
              </Box>
              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {texterFilterLabel}
              </Box>
            </FlexBox>
          </Typography>
        );
      }}
      onInputChange={(event, newSearch) => {
        // don't clear search when selecting options
        if (event?.type === "change" || event?.type === "blur") {
          setSearch(newSearch);
        }
      }}
      renderInput={params => (
        <FormTextField
          {...params}
          label={
            <Typography variant="body1Bold" sx={{ fontWeight: 600 }}>
              <FormattedMessage
                defaultMessage="Filter by Campaign or Texter"
                description="Label for campaign and texter filter"
              />
            </Typography>
          }
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment
                  position="start"
                  sx={{ padding: theme.spacing(1) }}
                >
                  <Search />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {campaignsLoading || textersLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              border: `1px solid ${palette.midGray} !important`,
              fontSize: "12px",
            },
          }}
        />
      )}
      PaperComponent={AutocompletePaper}
    />
  );
}
